import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";

const FORM_URL = "https://readdy.ai/api/form/db8gdu8kk00l6eltrjt0";

export default function AboutPage() {
  usePageTitle(
    "关于我们 - 91.con娱乐资讯平台",
    "了解91.con的网站定位、内容服务、联系方式与使用须知，并向我们提交反馈建议。",
  );

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", type: "内容纠错", message: "" });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const honey = (fd.get("contact_alt") as string | null)?.trim() || "";

    if (honey) {
      setStatus("success");
      return;
    }

    setStatus("loading");
    fd.delete("contact_alt");

    const urlencoded = new URLSearchParams();
    fd.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        urlencoded.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlencoded.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        (typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam")) ||
        (typeof serverMsg === "string" && serverMsg.includes("form data is spam"));

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setForm({ name: "", email: "", type: "内容纠错", message: "" });
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() !== ""
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <section className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="font-label text-xs font-semibold uppercase tracking-widest text-primary-400">
          ABOUT 91.CON
        </span>
        <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          多元娱乐资讯与内容平台
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-700">
          91.con 是一个集娱乐资讯新闻、视频资源展示与热点专题互动内容于一体的综合娱乐平台。我们希望为热爱影视、综艺、音乐与娱乐话题的用户，提供一个信息及时、内容多样、体验流畅的一站式娱乐内容入口。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            {
              icon: "ri-newspaper-line",
              title: "时效资讯",
              desc: "热点第一现场，影视、综艺、音乐与圈内动态持续更新。",
            },
            {
              icon: "ri-video-line",
              title: "多样视频",
              desc: "影视剧集、综艺节目与短视频精选，封面信息一目了然。",
            },
            {
              icon: "ri-fire-line",
              title: "热点专题",
              desc: "编辑精选聚合专题，资讯与视频一起看个通透。",
            },
          ].map((f) => (
            <div key={f.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/15 text-primary-300">
                <i className={`${f.icon} text-xl`}></i>
              </span>
              <h3 className="mt-3 font-heading text-lg font-semibold text-foreground-950">{f.title}</h3>
              <p className="mt-1.5 text-sm text-foreground-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* 联系方式 & 使用须知 */}
        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-customer-service-2-line text-primary-400"></i>联系方式
            </h2>
            <ul className="mt-4 flex flex-col gap-4 text-sm text-foreground-700">
              <li className="flex items-start gap-3">
                <i className="ri-mail-send-line mt-0.5 text-lg text-accent-400"></i>
                <div>
                  <div className="font-medium text-foreground-900">合作与投稿</div>
                  <div className="text-foreground-600">contact@91con.com</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-feedback-line mt-0.5 text-lg text-accent-400"></i>
                <div>
                  <div className="font-medium text-foreground-900">内容纠错与反馈</div>
                  <div className="text-foreground-600">使用右侧反馈表单提交，我们会尽快处理</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line mt-0.5 text-lg text-accent-400"></i>
                <div>
                  <div className="font-medium text-foreground-900">内容更新时段</div>
                  <div className="text-foreground-600">每日 09:00 - 24:00 持续更新</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-file-shield-2-line text-primary-400"></i>使用须知与隐私提示
            </h2>
            <ul className="mt-4 flex flex-col gap-3 text-sm leading-relaxed text-foreground-700">
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400"></i>
                本站当前所有资讯、视频与专题内容均为前端演示数据，用于展示页面结构与交互效果。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400"></i>
                搜索、筛选与视频播放为前端模拟演示，待后端数据接口接入后将提供真实内容。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400"></i>
                反馈表单仅收集你主动填写的信息，用于处理内容纠错与建议，不会用于其他用途。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400"></i>
                请勿在反馈中提交涉及个人隐私的敏感信息。
              </li>
            </ul>
          </div>
        </div>

        {/* 反馈表单 */}
        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-edit-box-line text-accent-400"></i>意见反馈
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            有内容纠错、建议或合作意向？填写下方表单告诉我们。
          </p>

          {status === "success" ? (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-accent-300/40 bg-accent-100/40 py-10 text-center">
              <i className="ri-checkbox-circle-fill text-4xl text-accent-500"></i>
              <p className="font-heading text-lg font-semibold text-foreground-950">提交成功，感谢你的反馈！</p>
              <p className="text-sm text-foreground-600">我们已收到你的信息，会尽快处理。</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-2 rounded-md border border-background-200 px-5 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                再提交一条
              </button>
            </div>
          ) : (
            <form data-readdy-form id="feedback-form" onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-sm text-foreground-700">
                    称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="你的称呼"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-sm text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fb-type" className="mb-1.5 block text-sm text-foreground-700">
                  反馈类型
                </label>
                <select
                  id="fb-type"
                  name="type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="内容纠错">内容纠错</option>
                  <option value="意见建议">意见建议</option>
                  <option value="合作投稿">合作投稿</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="mb-1.5 block text-sm text-foreground-700">
                  反馈内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="请描述你遇到的问题或建议（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
                ></textarea>
                <div className="mt-1 text-right text-xs text-foreground-500">
                  {form.message.length}/500
                </div>
              </div>

              {/* 反垃圾字段 */}
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-helper-note"
              />

              {status === "error" && (
                <div className="rounded-md border border-primary-400/40 bg-primary-100/40 px-4 py-2.5 text-sm text-primary-300">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>提交反馈
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}